import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { AirplaneTilt, Compass, CreditCard, EnvelopeSimple, Fingerprint, IdentificationCard, Scroll, Signature } from "@phosphor-icons/react";
import { Link, Redirect, useLocation, useParams } from "wouter";
import type { Onboarding, ProfileView } from "../../shared/types.ts";
import { Button, Card, PageHeader, StatusBadge, ease } from "../ui/index.ts";
import { errorText, saveProfile, type ProfilePatch } from "../lib/api.ts";
import { useApp } from "../lib/ctx.ts";
import { Loading } from "./parts.tsx";
import { AccountFields, AnswerFields, ContactFields, EducationFields, PrefFields, Resume, type Edit } from "./ProfileFields.tsx";
import { Billing } from "./settings/Billing.tsx";
import { DeleteAccount } from "./settings/DeleteAccount.tsx";
import { EmailRows } from "./settings/Emails.tsx";
import { Notice, Row, SettingsCard, SettingsNav } from "./settings/parts.tsx";

type SectionId = "profile" | "resume" | "preferences" | "answers" | "autopilot" | "emails" | "billing" | "account";

interface SectionDef {
  id: SectionId;
  title: string;
  icon: ReactNode;
  description: string;
  /** The onboarding step this section's Save finishes. */
  step?: number;
  /** What Save sends; a section without one has nothing to save. */
  patch?: (p: ProfileView) => ProfilePatch;
}

const SECTIONS: SectionDef[] = [
  {
    id: "profile",
    title: "Profile",
    icon: <IdentificationCard size={18} weight="duotone" />,
    description: "Who you are and how employers reach you. Forms ask for all of it.",
    step: 0,
    patch: (p) => ({
      firstName: p.firstName.trim(),
      lastName: p.lastName.trim(),
      email: p.email.trim(),
      phone: p.phone.trim(),
      pronouns: p.pronouns?.trim(),
      address: { city: p.address?.city?.trim(), state: p.address?.state?.trim(), postal: p.address?.postal?.trim() },
      links: { linkedin: p.links.linkedin?.trim(), github: p.links.github?.trim(), website: p.links.website?.trim() },
      education: p.education.filter((e) => e.school.trim()).map((e) => ({ ...e, school: e.school.trim() })),
    }),
  },
  {
    id: "resume",
    title: "Resume",
    icon: <Scroll size={18} weight="duotone" />,
    description: "headcount attaches it to every application.",
  },
  {
    id: "preferences",
    title: "Job preferences",
    icon: <Compass size={18} weight="duotone" />,
    description: "What headcount looks for when it ranks jobs.",
    step: 1,
    patch: (p) => ({
      prefs: {
        families: p.prefs.families,
        titles: p.prefs.titles,
        seniority: p.prefs.seniority,
        locations: p.prefs.locations,
        workplace: p.prefs.workplace,
        minSalary: p.prefs.minSalary ?? null,
        needsSponsorship: p.workAuth.needsSponsorship,
      },
      workAuth: { needsSponsorship: p.workAuth.needsSponsorship },
    }),
  },
  {
    id: "answers",
    title: "Application answers",
    icon: <Signature size={18} weight="duotone" />,
    description: "headcount gives these answers on every form that asks.",
    step: 2,
    patch: (p) => ({
      workAuth: { authorizedToWork: p.workAuth.authorizedToWork, citizenship: p.workAuth.citizenship?.trim() },
      relocation: { willing: p.relocation.willing },
      attestations: p.attestations,
      startDate: p.startDate?.trim(),
      salaryExpectation: p.salaryExpectation?.trim(),
    }),
  },
  {
    id: "autopilot",
    title: "Autopilot",
    icon: <AirplaneTilt size={18} weight="duotone" />,
    description: "headcount applies for you once setup is done. When a form asks something new, it asks you first.",
  },
  {
    id: "emails",
    title: "Emails",
    icon: <EnvelopeSimple size={18} weight="duotone" />,
    description: "Choose which emails headcount sends you. Emails about your account, like password resets, always send.",
  },
  {
    id: "billing",
    title: "Billing",
    icon: <CreditCard size={18} weight="duotone" />,
    description: "Your plan and this month's applications.",
  },
  {
    id: "account",
    title: "Account",
    icon: <Fingerprint size={18} weight="duotone" />,
    description: "How you sign in to headcount.",
  },
];

const CONTACT: Record<string, string> = { firstName: "name", lastName: "name", name: "name", email: "email", phone: "phone" };

/** What autopilot still needs, by section. Mirrors the Worker's onboarding check on the saved profile. */
function gaps(saved: ProfileView, ob: Onboarding | null) {
  const missing = ob?.missing ?? [];
  const contact = [...new Set(missing.map((m) => CONTACT[m]).filter((m): m is string => Boolean(m)))];
  const q = saved.prefs;
  const prefs =
    (saved.onboardingStep ?? 0) >= 2 || [q.families, q.titles, q.seniority, q.locations, q.workplace].some((a) => a.length > 0) || q.minSalary != null;
  const setup = !(ob?.done ?? true);
  return {
    contact,
    resume: missing.includes("resume") || !saved.hasResume,
    preferences: setup && !prefs,
    answers: setup && (saved.onboardingStep ?? 0) < 3,
  };
}

function overlay<T extends object>(base: T, patch: object): T {
  const out = { ...base } as Record<string, unknown>;
  for (const [k, v] of Object.entries(patch))
    out[k] = v && typeof v === "object" && !Array.isArray(v) ? overlay((out[k] as object | undefined) ?? {}, v) : v;
  return out as T;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function ProfileScreen() {
  const app = useApp();
  const [, navigate] = useLocation();
  const params = useParams<{ section?: string }>();
  const [draft, setDraft] = useState<ProfileView | null>(app.profile);
  const [saving, setSaving] = useState(false);
  const first = useRef(true);
  const ob = app.onboarding;
  const step = ob?.step ?? 0;
  const active = SECTIONS.find((s) => s.id === params.section);

  useEffect(() => {
    if (app.profile && !draft) setDraft(app.profile);
  }, [app.profile, draft]);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [params.section]);

  if (!draft) return <Loading label="Loading your settings" />;
  const saved = app.profile ?? draft;
  const need = gaps(saved, ob);
  const attention: Partial<Record<SectionId, boolean>> = {
    profile: need.contact.length > 0,
    resume: need.resume,
    preferences: need.preferences,
    answers: need.answers,
  };

  if (!active) {
    const next = ob && !ob.done ? SECTIONS.find((s) => attention[s.id]) : undefined;
    return <Redirect to={`/settings/${next?.id ?? "profile"}`} replace />;
  }

  const edit: Edit = { p: draft, set: (fn) => setDraft((d) => d && fn(d)) };
  const dirty = (s: SectionDef) => Boolean(s.patch && !same(s.patch(draft), s.patch(saved)));
  const advances = (s: SectionDef) => s.step !== undefined && s.step === step && !ob?.done;

  const save = async (s: SectionDef) => {
    if (!s.patch) return;
    setSaving(true);
    const advancing = advances(s);
    try {
      const p = await saveProfile({ ...s.patch(draft), ...(advancing ? { onboardingStep: Math.max(s.step! + 1, draft.onboardingStep ?? 0) } : {}) });
      const unsaved = SECTIONS.filter((t) => t !== s && dirty(t)).map((t) => t.patch!(draft));
      app.setProfile(p);
      setDraft(unsaved.reduce<ProfileView>(overlay, p));
      app.toast("Saved.", "ok");
      if (advancing) {
        const next = s.step === 0 ? (p.hasResume ? "preferences" : "resume") : s.step === 1 ? "answers" : null;
        navigate(next ? `/settings/${next}` : "/");
      }
    } catch (e) {
      app.toast(errorText(e), "bad");
    } finally {
      setSaving(false);
    }
  };

  const resumeDone = (u: ProfileView) => {
    setDraft((d) => d && { ...d, resumeName: u.resumeName, hasResume: u.hasResume });
    const contact = [u.firstName, u.lastName, u.email, u.phone].every((v) => v.trim());
    if (ob && !ob.done && step === 0 && contact) navigate("/settings/preferences");
  };

  const unanswered = [
    draft.attestations.convicted === undefined && "felony conviction",
    draft.attestations.formerEmployee === undefined && "worked there before",
  ].filter(Boolean);

  const notice: Partial<Record<SectionId, ReactNode>> = {
    profile: need.contact.length > 0 && <Notice>Missing for autopilot: {need.contact.join(", ")}.</Notice>,
    resume: need.resume && <Notice>Autopilot needs a resume before it can apply.</Notice>,
    answers: unanswered.length > 0 && <Notice>Not answered yet: {unanswered.join(", ")}.</Notice>,
  };

  const body: Record<SectionId, ReactNode> = {
    profile: (
      <>
        <ContactFields {...edit} />
        <EducationFields {...edit} />
      </>
    ),
    resume: (
      <Row label="Resume" hint="Saved as soon as it uploads.">
        <Resume name={draft.resumeName} onDone={resumeDone} />
      </Row>
    ),
    preferences: <PrefFields {...edit} />,
    answers: <AnswerFields {...edit} />,
    autopilot: <AutopilotRows need={need} ready={Boolean(ob?.done) && need.contact.length === 0 && !need.resume} />,
    emails: <EmailRows />,
    billing: null,
    account: (
      <>
        <AccountFields />
        <DeleteAccount />
      </>
    ),
  };

  return (
    <div className="flex flex-col gap-6 md:gap-8">
      <PageHeader title="Settings" description="Everything headcount puts on a form comes from here." />
      <div className="grid items-start gap-6 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-10">
        <SettingsNav
          active={active.id}
          items={SECTIONS.map((s) => ({ id: s.id, label: s.title, href: `/settings/${s.id}`, icon: s.icon, attention: attention[s.id] }))}
        />
        <div className="flex min-w-0 max-w-3xl flex-col gap-4">
          {ob && !ob.done && (
            <Card className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
              <StatusBadge tone="amber" dot>
                Step {Math.min(step + 1, 3)} of 3
              </StatusBadge>
              <p className="text-sm text-ink-soft">Finish setup and headcount starts applying.</p>
            </Card>
          )}
          <motion.div key={active.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease }}>
            {active.id === "billing" ? (
              <Billing title={active.title} description={active.description} />
            ) : (
              <SettingsCard
                title={active.title}
                description={active.description}
                notice={notice[active.id] || undefined}
                footer={
                  active.patch && (
                    <>
                      {dirty(active) && <span className="mr-auto text-[13px] text-muted">Unsaved changes</span>}
                      <Button loading={saving} disabled={saving || !(dirty(active) || advances(active))} onClick={() => save(active)}>
                        {!advances(active) ? "Save" : active.step === 2 ? "Save and finish" : "Save and continue"}
                      </Button>
                    </>
                  )
                }
              >
                {body[active.id]}
              </SettingsCard>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function AutopilotRows({ need, ready }: { need: ReturnType<typeof gaps>; ready: boolean }) {
  const todo: { label: string; to: SectionId }[] = [
    ...need.contact.map((c) => ({ label: c[0]!.toUpperCase() + c.slice(1), to: "profile" as const })),
    ...(need.resume ? [{ label: "Resume", to: "resume" as const }] : []),
    ...(need.preferences ? [{ label: "Job preferences", to: "preferences" as const }] : []),
    ...(need.answers ? [{ label: "Application answers", to: "answers" as const }] : []),
  ];
  return (
    <>
      <Row label="Status">
        <div className="flex h-10 items-center">
          <StatusBadge tone={ready ? "mint" : "amber"} dot>
            {ready ? "Ready to apply" : "Paused"}
          </StatusBadge>
        </div>
      </Row>
      {todo.length > 0 && (
        <Row label="Still needed">
          <ul className="flex flex-wrap gap-x-4 gap-y-1 pt-2.5 text-sm font-semibold">
            {todo.map((t) => (
              <li key={t.label}>
                <Link href={`/settings/${t.to}`} className="text-primary-ink underline-offset-2 hover:underline">
                  {t.label}
                </Link>
              </li>
            ))}
          </ul>
        </Row>
      )}
    </>
  );
}
